import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';

import { ROUTE } from 'src/constants/routes';
import type { AppDispatch } from 'src/store/store';
import { ReduxState } from 'src/store/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import {
  VoiceboxMode,
  createInitialVoiceboxState,
} from 'src/store/voicebox/types';
import { INITIAL_EXISTING_MODEL_PROGRESS_MESSAGE } from 'src/utils/voicebox/prompts/existingModel';
import { INITIAL_SYSTEM_MESSAGES } from 'src/utils/voicebox/prompts/initialMessages';
import { INITIAL_NEW_MODEL_PROGRESS_MESSAGE } from 'src/utils/voicebox/prompts/newModel';
import { wrappedVoiceboxRequest } from 'src/utils/voicebox/request/wrappedVoiceboxRequest';
import {
  VoiceboxMessage,
  VoiceboxModelSchema,
  VoiceboxWrapperRequest,
  VoiceboxWrapperResponse,
} from 'src/utils/voicebox/types';

export const voiceboxSlice = createSlice({
  name: 'voicebox',
  initialState: createInitialVoiceboxState,
  reducers: {
    exportedVoiceboxConversation: (
      _state,
      _payload: PayloadAction<{ filename: string }>
    ) => {},

    setVoiceboxMode: (
      state,
      {
        payload: { mode, openAiKey },
      }: PayloadAction<{
        openAiKey: string;
        mode: VoiceboxMode;
        modelSchema?: VoiceboxModelSchema;
      }>
    ) => {
      state.mode = mode;
      state.openAiKey = openAiKey;
      state.pending = false;

      const conversationId = uuid();
      state.conversationId = conversationId;

      const messages = [...INITIAL_SYSTEM_MESSAGES];
      if (mode === VoiceboxMode.NEW) {
        messages.push(INITIAL_NEW_MODEL_PROGRESS_MESSAGE);
      } else {
        messages.push(INITIAL_EXISTING_MODEL_PROGRESS_MESSAGE);
      }
      state.conversationMessages = messages;
    },

    updatedVoiceboxConversation: (
      state,
      {
        payload,
      }: PayloadAction<{
        conversationId: string;
        messages: VoiceboxMessage[];
      }>
    ) => {
      if (state.conversationId === payload.conversationId) {
        state.conversationMessages = payload.messages;
      }
    },
  },
  extraReducers: ({ addCase }) => {
    // TODO when should the voicebox clear state?
    addCase(uiSlice.actions.changedLocation, (state, action) => {
      let projectId = '';
      if (action.payload.route !== ROUTE.HOME) {
        const routeParts = (action.payload.route || '').split('/');
        projectId = routeParts[2] || '';
      }

      if (!projectId || state.projectId !== projectId) {
        state.mode = null;
        state.conversationId = '';
        state.conversationMessages = [];
        state.pending = false;
      }

      state.projectId = projectId;
    });

    addCase(askVoicebox.pending, (state, action) => {
      if (state.conversationId === action.meta.arg.conversationId) {
        state.pending = true;
      }
    });

    addCase(askVoicebox.rejected, (state, action) => {
      if (state.conversationId === action.meta.arg.conversationId) {
        state.pending = false;
      }
    });

    addCase(askVoicebox.fulfilled, (state, action) => {
      if (state.conversationId === action.meta.arg.conversationId) {
        state.pending = false;
      }
    });
  },
});

export const askVoicebox = createAsyncThunk<
  VoiceboxWrapperResponse,
  VoiceboxWrapperRequest,
  { dispatch: AppDispatch; state: ReduxState }
>('requestVoiceboxThunk', async (payload, { dispatch, getState }) => {
  const {
    voicebox: { openAiKey, conversationMessages },
  } = getState();

  const onUpdateMessages = (messages: VoiceboxMessage[]) => {
    dispatch(
      voiceboxSlice.actions.updatedVoiceboxConversation({
        conversationId: payload.conversationId,
        messages: [...messages],
      })
    );
  };

  return await wrappedVoiceboxRequest(payload, {
    // TODO: use project namespace
    defaultNamespace: undefined,
    openAiKey,
    onUpdateMessages,
    initialMessages: conversationMessages,
  });
});
